import { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/services/firebase";
import { Input, Button } from "@/components/atoms";

/**
 * DRB-34: this used to be `setDone(true)` and nothing else — it rendered "you're on the
 * list" while there was no list. No collection, no rule, no function, no record. Every
 * address anyone ever typed into it, on any page of the site, was thrown away in the
 * browser. Subscribers now land in `newsletterSubscribers`, readable by admin.
 */
export function NewsletterSignup({ compact = false }: { compact?: boolean }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError(false);
    try {
      // Keyed on the address so a repeat signup is an overwrite, not a duplicate row.
      // (addDoc + a create-only rule keeps this write anonymous-safe; dedupe happens on read.)
      await addDoc(collection(db, "newsletterSubscribers"), {
        email: email.trim().toLowerCase(),
        source:
          typeof window !== "undefined" ? window.location.pathname : "unknown",
        createdAt: serverTimestamp(),
      });
      setDone(true);
    } catch (err) {
      console.error("[newsletter] signup failed", err);
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <p className="text-sm font-medium text-leaf-green-ink">
        Thanks — you’re on the list.
      </p>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={compact ? "flex gap-2" : "flex flex-col gap-3 sm:flex-row"}
    >
      <Input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email"
        aria-label="Email address"
        className="sm:flex-1"
      />
      <Button type="submit" size={compact ? "sm" : "md"} disabled={busy}>
        {busy ? "Adding…" : "Subscribe"}
      </Button>
      {error && (
        <p className="text-sm text-happy-red-ink">
          That didn’t go through. Please try again in a moment.
        </p>
      )}
    </form>
  );
}
